import * as React from "react";
import { NavLink, useLocation, useResolvedPath } from "react-router-dom";

import { FOCUS_RING_INSET, cx } from "./controlStyles";

// Underline tabs = the SECTIONS of a page (Settings, Code, an agent's
// workspace). For view switches and filters use Segmented instead.
//
//   <TabStrip ariaLabel="Settings"><Tab active onClick>Mail</Tab>…</TabStrip>
//   <NavTabStrip ariaLabel="Code"><NavTab to="/code/sessions" icon={…} count={3}>Sessions</NavTab></NavTabStrip>
//   <Tabs value={tab} onChange={setTab} ariaLabel="Skills"
//         items={[{ value: "all", label: "All", count: 12 }, …]} />
//
// 40px tall (44px on touch screens), 14px medium text, muted until active;
// the active tab is primary text over a 2px accent underline. Counts are
// 20px pills, toned like the sidebar badges: "attention" an accent tint
// (a solid accent disc read as the selected tab in Midnight and as an
// error in Earth), "warning" amber, "danger" the solid danger fill
// (something is broken - the loudest).
// The strip scrolls sideways on narrow screens instead of wrapping, and
// keeps the active tab in view: a page opened on its seventh tab at 390px
// shows that tab, not the first three (the strip scrolls itself, never the
// page).
//
// TabStrip / Tab / Tabs switch panels in place: a role="tablist" where
// Arrow Left/Right, Home and End move focus (roving tabindex: one tab stop
// per strip) and Enter / Space activates. NavTabStrip / NavTab go to
// another URL, so they are a <nav> of ordinary links (aria-current on the
// current one, every link in the Tab order) that only LOOK like tabs.

export type TabCountTone = "default" | "attention" | "warning" | "danger";

const COUNT_TONE: Record<TabCountTone, string> = {
  default: "bg-raised text-fg-muted",
  attention: "bg-accent-500/15 text-accent-fg",
  warning: "bg-warning/15 text-warning",
  danger: "bg-danger-fill text-on-danger",
};

export function TabCount({
  count,
  tone = "default",
  className,
}: {
  count: number | string;
  tone?: TabCountTone;
  className?: string;
}) {
  return (
    <span
      className={cx(
        "inline-grid h-[20px] min-w-[20px] place-items-center rounded-pill px-1.5 text-xs font-semibold tabular-nums",
        COUNT_TONE[tone],
        className,
      )}
    >
      {count}
    </span>
  );
}

// Move focus between the tabs of one strip with the arrow keys.
function onStripKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
  const keys = ["ArrowLeft", "ArrowRight", "Home", "End"];
  if (!keys.includes(e.key)) return;
  const tabs = Array.from(
    e.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]:not([aria-disabled="true"])'),
  );
  if (!tabs.length) return;
  const i = tabs.indexOf(document.activeElement as HTMLElement);
  let next = i;
  if (e.key === "ArrowLeft") next = i <= 0 ? tabs.length - 1 : i - 1;
  if (e.key === "ArrowRight") next = i === -1 || i === tabs.length - 1 ? 0 : i + 1;
  if (e.key === "Home") next = 0;
  if (e.key === "End") next = tabs.length - 1;
  e.preventDefault();
  tabs[next]?.focus();
  tabs[next]?.scrollIntoView({ block: "nearest", inline: "nearest" });
}

/** Scroll a strip sideways so its active tab is visible; the page never moves. */
function revealActive(strip: HTMLElement | null) {
  if (!strip || strip.scrollWidth <= strip.clientWidth) return;
  const tab = strip.querySelector<HTMLElement>('[aria-selected="true"], [aria-current="page"]');
  if (!tab) return;
  const box = strip.getBoundingClientRect();
  const r = tab.getBoundingClientRect();
  // A little of the neighbour shows, so it reads as a strip that scrolls.
  const pad = 24;
  if (r.left < box.left) strip.scrollLeft -= box.left - r.left + pad;
  else if (r.right > box.right) strip.scrollLeft += r.right - box.right + pad;
}

/** Re-reveal whenever `active` (whatever identifies the active tab) changes, and on mount. */
function useRevealActive(active: unknown) {
  const ref = React.useRef<HTMLDivElement>(null);
  React.useLayoutEffect(() => revealActive(ref.current), [active]);
  return ref;
}

/**
 * Fade the edge(s) a strip can still scroll towards, so a tab cut off at
 * 390px reads as "more this way" (the strip hides its scrollbar).
 */
export function useScrollFade(ref: React.RefObject<HTMLElement>, content: unknown): React.CSSProperties | undefined {
  const [edges, setEdges] = React.useState({ left: false, right: false });
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const left = el.scrollLeft > 1;
      const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      setEdges((e) => (e.left === left && e.right === right ? e : { left, right }));
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro?.disconnect();
    };
  }, [ref, content]);
  if (!edges.left && !edges.right) return undefined;
  const mask = `linear-gradient(to right, ${edges.left ? "transparent, black 28px" : "black"}, ${edges.right ? "black calc(100% - 28px), transparent" : "black"})`;
  return { maskImage: mask, WebkitMaskImage: mask };
}

/** The index of the active <Tab> among a strip's children (-1: none). */
function activeIndex(children: React.ReactNode): number {
  return React.Children.toArray(children).findIndex((c) => React.isValidElement<TabProps>(c) && c.type === Tab && !!c.props.active);
}

// Exactly one tab stop per strip: the active tab, else the first enabled
// Tab child (worked out in render, so React owns every tabIndex).
function withTabStop(children: React.ReactNode): React.ReactNode {
  const items = React.Children.toArray(children);
  const isTab = (c: React.ReactNode): c is React.ReactElement<TabProps> =>
    React.isValidElement(c) && c.type === Tab;
  if (items.some((c) => isTab(c) && c.props.active)) return items;
  const first = items.findIndex((c) => isTab(c) && !c.props.disabled);
  if (first < 0) return items;
  return items.map((c, i) => (i === first && isTab(c) ? React.cloneElement(c, { tabStop: true }) : c));
}

interface TabStripProps {
  children: React.ReactNode;
  className?: string;
  /** Accessible name of the tab list ("Settings sections"). */
  ariaLabel?: string;
  /** "page" pads the strip to the page gutter (px-4 md:px-8); "none" for use inside a container. */
  inset?: "page" | "none";
}

function stripClass(inset: TabStripProps["inset"], className?: string) {
  return cx(
    // The hairline is an inset shadow (not a border) so the active
    // underline can sit on top of it without overflowing the scroller.
    "flex items-end gap-1 overflow-x-auto overflow-y-hidden whitespace-nowrap scrollbar-none",
    "shadow-[inset_0_-1px_0_var(--c-border-subtle)]",
    inset === "page" && "px-4 md:px-8",
    className,
  );
}

export function TabStrip({ children, className, ariaLabel, inset = "page" }: TabStripProps) {
  const ref = useRevealActive(activeIndex(children));
  const fade = useScrollFade(ref, React.Children.count(children));
  return (
    <div
      ref={ref}
      style={fade}
      role="tablist"
      aria-label={ariaLabel}
      aria-orientation="horizontal"
      onKeyDown={onStripKeyDown}
      className={stripClass(inset, className)}
    >
      {withTabStop(children)}
    </div>
  );
}

/** A row of links styled as tabs (each one a page of its own). */
export function NavTabStrip({ children, className, ariaLabel = "Sections", inset = "page" }: TabStripProps) {
  const ref = useRevealActive(useLocation().pathname);
  const fade = useScrollFade(ref, React.Children.count(children));
  return (
    <nav ref={ref} style={fade} aria-label={ariaLabel} className={stripClass(inset, className)}>
      {children}
    </nav>
  );
}

interface TabLookProps {
  active?: boolean;
  icon?: React.ReactNode;
  count?: number | string;
  countTone?: TabCountTone;
  /** Read in place of the bare count ("2 apps need you"), so the number never relies on its colour. */
  countLabel?: string;
  children: React.ReactNode;
}

const TAB_CLASS =
  "relative inline-flex h-[40px] shrink-0 items-center gap-2 px-2.5 text-base font-medium transition-colors [@media(pointer:coarse)]:h-[44px] " +
  FOCUS_RING_INSET;

function TabInner({ active, icon, count, countTone, countLabel, children }: TabLookProps) {
  return (
    <>
      {icon && (
        <span aria-hidden className="flex shrink-0 items-center [&>svg]:h-4 [&>svg]:w-4">
          {icon}
        </span>
      )}
      <span>{children}</span>
      {count !== undefined && count !== null && count !== "" &&
        (countLabel ? (
          <>
            <span aria-hidden className="inline-flex">
              <TabCount count={count} tone={countTone} />
            </span>
            <span className="sr-only">, {countLabel}</span>
          </>
        ) : (
          <TabCount count={count} tone={countTone} />
        ))}
      <span
        aria-hidden
        className={cx(
          "absolute inset-x-1.5 bottom-0 h-[2px] rounded-pill",
          active ? "bg-[color:var(--c-accent-light)]" : "bg-transparent",
        )}
      />
    </>
  );
}

function tabColour(active?: boolean, disabled?: boolean) {
  if (disabled) return "text-fg-subtle opacity-60 cursor-default";
  return active ? "text-fg" : "text-fg-muted [@media(hover:hover)]:hover:text-fg";
}

interface TabProps extends TabLookProps {
  onClick?: () => void;
  disabled?: boolean;
  id?: string;
  /** id of the panel this tab shows (aria-controls). */
  controls?: string;
  className?: string;
  /** Set by the strip: the tab stop when no tab is active. */
  tabStop?: boolean;
}

export function Tab({
  active,
  onClick,
  children,
  icon,
  count,
  countTone,
  disabled,
  id,
  controls,
  className,
  tabStop,
}: TabProps) {
  return (
    <button
      type="button"
      id={id}
      role="tab"
      aria-selected={!!active}
      aria-controls={controls}
      aria-disabled={disabled || undefined}
      tabIndex={active || tabStop ? 0 : -1}
      onClick={disabled ? undefined : onClick}
      className={cx(TAB_CLASS, tabColour(active, disabled), className)}
    >
      <TabInner active={active} icon={icon} count={count} countTone={countTone}>
        {children}
      </TabInner>
    </button>
  );
}

interface NavTabProps extends Omit<TabLookProps, "active"> {
  to: string;
  end?: boolean;
  className?: string;
}

export function NavTab({ to, end, children, icon, count, countTone, countLabel, className }: NavTabProps) {
  const resolved = useResolvedPath(to);
  const location = useLocation();
  const active = end
    ? location.pathname === resolved.pathname
    : location.pathname === resolved.pathname ||
      location.pathname.startsWith(resolved.pathname + "/");
  return (
    <NavLink
      to={to}
      end={end}
      aria-current={active ? "page" : undefined}
      className={cx(TAB_CLASS, tabColour(active), className)}
    >
      <TabInner active={active} icon={icon} count={count} countTone={countTone} countLabel={countLabel}>
        {children}
      </TabInner>
    </NavLink>
  );
}

export interface TabItem<T extends string> {
  value: T;
  label: React.ReactNode;
  icon?: React.ReactNode;
  count?: number | string;
  countTone?: TabCountTone;
  disabled?: boolean;
}

interface TabsProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  items: TabItem<T>[];
  ariaLabel: string;
  inset?: "page" | "none";
  className?: string;
  /** Prefix for tab / panel ids, so panels can point back with aria-labelledby. */
  idPrefix?: string;
}

/** Data-driven tab strip: one call per page. */
export function Tabs<T extends string>({
  value,
  onChange,
  items,
  ariaLabel,
  inset = "none",
  className,
  idPrefix,
}: TabsProps<T>) {
  return (
    <TabStrip ariaLabel={ariaLabel} inset={inset} className={className}>
      {items.map((item) => (
        <Tab
          key={item.value}
          id={idPrefix ? `${idPrefix}-tab-${item.value}` : undefined}
          controls={idPrefix ? `${idPrefix}-panel-${item.value}` : undefined}
          active={item.value === value}
          onClick={() => onChange(item.value)}
          icon={item.icon}
          count={item.count}
          countTone={item.countTone}
          disabled={item.disabled}
        >
          {item.label}
        </Tab>
      ))}
    </TabStrip>
  );
}
